import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTariff } from "../context/TariffContext";
import { useUserInfo } from "../context/UserInfoContext";
import EN from "../locales/en";
import Button from "./Button";
import ErrorIcon from "./icons/ErrorIcon";

const AddressSelect = ({ fullTerms }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const {
    addressId,
    setAddressId,
    address,
    setAddress,
    addressResults,
    fetchingAddressResults,
    isEditingPostcode,
    addressLookupError,
    setShowAddressPageError,
    showAddressEmptyWarning,
    setShowAddressEmptyWarning,
    setGasOnlyAddressError,
  } = useUserInfo();
  const { fetchDetails, resetTariffInfo } = useTariff();

  const handleSelectChange = (event) => {
    if (!event.target.value) {
      setAddressId("");
      setAddress("");
      setShowAddressEmptyWarning(true);
      return;
    }
    const { id, address } = JSON.parse(event.target.value);
    setAddressId(id);
    setAddress(address);
    setShowAddressEmptyWarning(false);
    setShowAddressPageError(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    resetTariffInfo();
    if (!addressId) {
      setShowAddressEmptyWarning(true);
      return;
    } else {
      setLoading(true);
      try {
        const termsOfUse = {
          page: "/address",
          terms_copy: `${fullTerms?.title}\n\n${fullTerms?.body}`,
          via: "Terms of use modal",
        };

        const details = await fetchDetails(addressId, termsOfUse);
        const mpan = details?.mpan;
        const mprn = details?.mprn;

        if (mpan) {
          if (mprn) {
            navigate(`/energy-usage/${mpan}/${mprn}`);
          } else {
            navigate(`/energy-usage/${mpan}`);
          }
        } else {
          setGasOnlyAddressError(true);
        }
      } catch (error) {
        setLoading(false);
        setShowAddressPageError(true);
        console.error(error);
      }
    }
  };

  const hasErrors = addressLookupError || showAddressEmptyWarning;

  return (
    addressResults?.length > 0 &&
    !fetchingAddressResults &&
    !isEditingPostcode && (
      <form onSubmit={submit} className="w-full max-w-lg mt-4">
        <label
          htmlFor="address"
          className={`block text-md font-bold leading-[1.5] mb-1 ${
            hasErrors ? "text-red-500" : ""
          }`}
        >
          Select Address
        </label>
        <select
          id="address"
          name="address"
          className={`w-full p-4 border rounded-lg focus:outline-none focus:ring-2 ${
            hasErrors
              ? "border-red-500 focus:ring-red-500 focus:border-red-500"
              : "border-[#afb6b9] focus:ring-primary focus:border-primary"
          }`}
          onChange={handleSelectChange}
          defaultValue={JSON.stringify({ id: addressId, address })}
          disabled={loading}
        >
          <option value="">Select an address</option>
          {addressResults?.map((result) => (
            <option
              key={result.addressId}
              value={JSON.stringify({
                id: result.addressId,
                address: result.text,
              })}
            >
              {result.text}
            </option>
          ))}
        </select>

        {hasErrors && (
          <div className="mt-3 flex justify-start items-start">
            <ErrorIcon />
            <p className="flex-1 text-red-500 text-sm">
              {showAddressEmptyWarning
                ? EN.address.address_empty_text
                : EN.address.address_error_text}
            </p>
          </div>
        )}

        <div className="mt-8">
          <Button type="submit" disabled={loading} fullWidth>
            {loading ? EN.address.submitting_text : EN.address.continue_text}
          </Button>
        </div>
      </form>
    )
  );
};

export default AddressSelect;
